import {
    FLAT_RATE_PLANS,
    PLAN_BASE_FEATURES,
    PREMIUM_FEATURES,
} from "@common/flatRateBilling";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { FC, useMemo } from "react";
import { useFormikContext } from "formik";
import classNames from "classnames";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { Tag } from "@design-system/Tag/Tag";
import { ClientPricing } from "@hooks/billing/useClientPricing";
import { discountLabel } from "@helpers/billing";
import { ClientFormSchema } from "../AddClientSteps/lib";
import {
    buildPlanStepOptions,
    getAutoSelectedPlan,
    isOrganizationOwnedPricingBlocked,
} from "../AddClientSteps/planHelpers";
import { OrganizationOwnedPricingStatus } from "../AddClientSteps/OrganizationOwnedPricingStatus";
import { V3BillingPreview } from "../AddClientSteps/V3BillingPreview";
import styles from "./PlanStep.module.scss";
import drawerStyles from "./AddClientDrawerSteps.module.scss";

type BillingOwnershipValue = "true" | "false";

const fields = createFormikFields<ClientFormSchema>();

interface PlanStepProps {
    clientPricing: ClientPricing;
}

function planIncludesFeature(
    plan: FLAT_RATE_PLANS,
    feature: PREMIUM_FEATURES,
): boolean {
    return PLAN_BASE_FEATURES[plan].includes(feature);
}

export const PlanStep: FC<PlanStepProps> = ({ clientPricing }) => {
    const { values, setFieldValue, handleBlur } =
        useFormikContext<ClientFormSchema>();
    const allowedPlans = useActiveOrganization()?.allowedPlans;
    const organizationBillingTag = clientPricing.percentOffDiscount ? (
        <Tag color="accent" outline>
            {discountLabel(clientPricing.percentOffDiscount)}
        </Tag>
    ) : null;

    const options = useMemo(
        () =>
            buildPlanStepOptions({
                clientPricing,
                isOrgOwned: values.plan.billingOwnership,
                allowedPlans,
            }),
        [clientPricing, values.plan.billingOwnership, allowedPlans],
    );

    const isPricingBlocked = isOrganizationOwnedPricingBlocked({
        clientPricing,
        isOrgOwned: values.plan.billingOwnership,
    });

    const isMultiEntityRequired = Boolean(values.isMultiEntity);
    const isAccrualRequired = Boolean(values.isAccrualLedgerEnabled);
    const plusLockedReason = isMultiEntityRequired
        ? "Required for multi-entity"
        : "Required for accrual ledger";

    const isPlanLocked = (plan: FLAT_RATE_PLANS): boolean => {
        if (
            isMultiEntityRequired &&
            !planIncludesFeature(plan, PREMIUM_FEATURES.UNLIMITED_ENTITIES)
        ) {
            return true;
        }

        return (
            isAccrualRequired &&
            !planIncludesFeature(plan, PREMIUM_FEATURES.ACCRUAL_LEDGER)
        );
    };

    const firstPlanWithFeature = (feature: PREMIUM_FEATURES) =>
        options.find(
            (candidate) =>
                !isPlanLocked(candidate.value) &&
                planIncludesFeature(candidate.value, feature),
        )?.value;

    const firstMultiEntityPlan = firstPlanWithFeature(
        PREMIUM_FEATURES.UNLIMITED_ENTITIES,
    );
    const firstAccrualPlan = firstPlanWithFeature(
        PREMIUM_FEATURES.ACCRUAL_LEDGER,
    );

    const handleBillingOwnershipChange = (
        value: BillingOwnershipValue,
    ): void => {
        const newIsOrgOwned = value === "true";
        setFieldValue("plan.billingOwnership", newIsOrgOwned);
        handleBlur("plan.billingOwnership");

        const autoSelectedPlan = getAutoSelectedPlan(
            newIsOrgOwned,
            values.plan.plan,
            allowedPlans,
        );
        if (autoSelectedPlan) {
            setFieldValue("plan.plan", autoSelectedPlan);
            handleBlur("plan.plan");
        }
    };

    const handlePlanChange = (value: FLAT_RATE_PLANS): void => {
        if (isPlanLocked(value)) {
            return;
        }

        setFieldValue("plan.plan", value);
        handleBlur("plan.plan");
    };

    return (
        <div className={drawerStyles.container}>
            <section className={styles.section}>
                <header className={drawerStyles.header}>
                    Who is paying for the subscription?
                </header>
                <RadioGroup<BillingOwnershipValue>
                    name="billingOwnership"
                    aria-label="Subscription payer"
                    value={values.plan.billingOwnership ? "true" : "false"}
                    onValueChange={handleBillingOwnershipChange}
                    className={classNames(styles.radioGroup, styles.stacked)}
                >
                    <Radio
                        className={styles.radio}
                        label={
                            <div className={styles.planName}>
                                Bill my firm
                                {organizationBillingTag}
                            </div>
                        }
                        value="true"
                    />

                    <Radio
                        className={styles.radio}
                        label="Bill client"
                        value="false"
                    />
                </RadioGroup>
                <div
                    className={styles.billingTransferEmailSlot}
                    data-open={values.plan.billingOwnership ? undefined : ""}
                    inert={values.plan.billingOwnership}
                >
                    <div className={styles.billingTransferEmailClip}>
                        <fields.Text
                            className={styles.billingTransferEmail}
                            size="xl"
                            name="billingTransfer.email"
                            label="Client's email address"
                        />
                    </div>
                </div>
            </section>

            <section className={styles.section}>
                <header className={drawerStyles.header}>Select plan</header>
                {isPricingBlocked ? (
                    <OrganizationOwnedPricingStatus
                        clientPricing={clientPricing}
                        isOrgOwned={values.plan.billingOwnership}
                    />
                ) : (
                    <RadioGroup<FLAT_RATE_PLANS>
                        name="plan"
                        aria-label="Plan"
                        value={values.plan.plan}
                        onValueChange={handlePlanChange}
                        className={classNames(
                            styles.radioGroup,
                            styles.stacked,
                        )}
                    >
                        {options.map((option) => {
                            const isPlusLocked = isPlanLocked(option.value);
                            const hasRequirementTag = isMultiEntityRequired
                                ? option.value === firstMultiEntityPlan
                                : isAccrualRequired &&
                                  option.value === firstAccrualPlan;

                            return (
                                <Radio
                                    className={styles.radio}
                                    key={option.value}
                                    disabled={isPlusLocked}
                                    label={
                                        <>
                                            <div className={styles.main}>
                                                <div
                                                    className={styles.planName}
                                                >
                                                    {option.label}

                                                    {option.isComplimentary && (
                                                        <Tag color="neutral">
                                                            Complimentary
                                                        </Tag>
                                                    )}

                                                    {hasRequirementTag && (
                                                        <Tag color="neutral">
                                                            {plusLockedReason}
                                                        </Tag>
                                                    )}
                                                </div>
                                                <small
                                                    className={
                                                        styles.description
                                                    }
                                                >
                                                    {option.description}
                                                </small>
                                            </div>
                                            <aside className={styles.addon}>
                                                {option.addon}
                                            </aside>
                                        </>
                                    }
                                    value={option.value}
                                />
                            );
                        })}
                    </RadioGroup>
                )}
                <V3BillingPreview
                    clientPricing={clientPricing}
                    plan={values.plan.plan}
                    isOrgOwned={values.plan.billingOwnership}
                />
            </section>
        </div>
    );
};
